import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { analiseService, AnaliseDetalheDto, AnaliseImpacto } from '../../../services/analiseService';

const IMPACTOS: { value: AnaliseImpacto; label: string; classes: string }[] = [
  { value: 'POSITIVO', label: 'Positivo', classes: 'bg-green-100 text-green-800 border-green-200' },
  { value: 'NEGATIVO', label: 'Negativo', classes: 'bg-red-100 text-red-800 border-red-200' },
  { value: 'NEUTRO', label: 'Neutro', classes: 'bg-gray-100 text-gray-800 border-gray-200' },
];

const normalizeImpacto = (value?: string | null): AnaliseImpacto => {
  const upper = (value || '').toUpperCase();
  if (upper.includes('POSITIVO')) return 'POSITIVO';
  if (upper.includes('NEGATIVO')) return 'NEGATIVO';
  return 'NEUTRO';
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
};

const AnalysisDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { analysisId } = useParams<{ analysisId: string }>();

  const decodedAnalysisId = useMemo(() => decodeURIComponent(analysisId || ''), [analysisId]);

  const [analysis, setAnalysis] = useState<AnaliseDetalheDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [selectedImpacto, setSelectedImpacto] = useState<AnaliseImpacto>('NEUTRO');
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!decodedAnalysisId) {
        setIsLoading(false);
        setLoadError('Identificador da análise não informado.');
        return;
      }
      setIsLoading(true);
      setLoadError(null);
      try {
        const data = await analiseService.getByTexto(decodedAnalysisId);
        if (cancelled) return;
        if (!data) {
          setLoadError('Não foi possível localizar os detalhes dessa análise.');
          setAnalysis(null);
          return;
        }
        setAnalysis(data);
        setSelectedImpacto(normalizeImpacto(data.impacto));
      } catch (error) {
        console.error('Erro ao carregar detalhes da análise:', error);
        if (!cancelled) setLoadError('Erro ao carregar detalhes da análise.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [decodedAnalysisId]);

  const currentImpacto = analysis ? normalizeImpacto(analysis.impacto) : 'NEUTRO';
  const hasChanges = selectedImpacto !== currentImpacto;

  const handleSave = async () => {
    if (!analysis || !hasChanges) return;
    setIsSaving(true);
    setSaveError(null);
    setSaveMessage(null);
    try {
      const updated = await analiseService.updateImpacto(analysis.analise, selectedImpacto);
      if (updated) {
        setAnalysis(updated);
        setSelectedImpacto(normalizeImpacto(updated.impacto));
        setSaveMessage('Impacto atualizado com sucesso.');
      }
    } catch (error) {
      console.error('Erro ao salvar impacto:', error);
      setSaveError('Não foi possível atualizar o impacto.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen-1/2 gap-4 p-6">
        <div className="w-12 h-12 border-4 border-gray-200 border-t-blue-700 rounded-full animate-spin"></div>
        <p>Carregando análise...</p>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="p-4 md:p-6">
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h1 className="text-xl font-bold text-gray-900 mb-2">Análise não encontrada</h1>
          <p className="text-gray-600 mb-4">{loadError || 'Não foi possível localizar os detalhes dessa análise.'}</p>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-4 py-2 rounded-lg bg-blue-700 text-white font-semibold hover:bg-blue-800 transition-colors"
          >
            Voltar
          </button>
        </div>
      </div>
    );
  }

  const currentImpactoMeta = IMPACTOS.find((i) => i.value === currentImpacto);

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Voltar
        </button>
      </div>

      <div className="bg-white rounded-xl p-5 md:p-7 border border-gray-200 shadow-sm mb-6">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className="text-xs md:text-sm text-blue-700 font-semibold">
            {analysis.empresa?.nome || analysis.empresa?.tickerPrincipal || 'Empresa não informada'}
          </span>
          <span className="text-xs md:text-sm text-gray-500">{formatDateTime(analysis.dataProcessamento)}</span>
          {currentImpactoMeta && (
            <span className={`text-xs font-semibold px-2 py-1 rounded-full border ${currentImpactoMeta.classes}`}>
              {currentImpactoMeta.label}
            </span>
          )}
        </div>
        <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">
          {analysis.fato || 'Análise de mercado'}
        </h1>
        <div className="space-y-3">
          <div className="bg-gray-50 rounded-lg border border-gray-100 p-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Fato</p>
            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
              {analysis.fato || 'Fato não informado.'}
            </p>
          </div>
          <div className="bg-blue-50/70 rounded-lg border border-blue-100 p-3">
            <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-1">Análise</p>
            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">{analysis.analise}</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl p-5 md:p-7 border border-gray-200 shadow-sm mb-6">
        <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-3">Impacto</h2>
        <p className="text-sm text-gray-600 mb-4">
          Ajuste o impacto desta análise caso precise corrigir a classificação automática.
        </p>
        <div className="flex flex-wrap gap-2 mb-4">
          {IMPACTOS.map((item) => {
            const isSelected = selectedImpacto === item.value;
            return (
              <button
                key={item.value}
                type="button"
                onClick={() => setSelectedImpacto(item.value)}
                className={`px-4 py-2 rounded-lg border text-sm font-semibold transition-colors ${
                  isSelected
                    ? `${item.classes} ring-2 ring-offset-1 ring-blue-500`
                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                }`}
              >
                {item.label}
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
            className="px-4 py-2 rounded-lg bg-blue-700 text-white text-sm font-semibold hover:bg-blue-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Salvando...' : 'Salvar alterações'}
          </button>
          {saveMessage && <span className="text-sm text-green-700">{saveMessage}</span>}
          {saveError && <span className="text-sm text-red-700">{saveError}</span>}
        </div>
      </div>

      <div className="bg-white rounded-xl p-5 md:p-7 border border-gray-200 shadow-sm">
        <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4">
          Notícias associadas ({analysis.noticias?.length || 0})
        </h2>

        {analysis.noticias && analysis.noticias.length > 0 ? (
          <div className="space-y-3">
            {analysis.noticias.map((news, index) => (
              <a
                key={`${news.url}-${index}`}
                href={news.url}
                target="_blank"
                rel="noreferrer"
                className="block rounded-lg border border-gray-200 p-4 hover:border-blue-200 hover:bg-blue-50/40 transition-colors"
              >
                <p className="text-sm md:text-base font-semibold text-gray-900 mb-1">{news.titulo}</p>
                <p className="text-xs text-gray-500">{formatDateTime(news.data)}</p>
              </a>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-600">Nenhuma notícia associada encontrada para esta análise.</p>
        )}
      </div>
    </div>
  );
};

export default AnalysisDetailPage;
